import { addDays, startOfDay, subDays } from 'date-fns';
import { SchedulerDateRange, SchedulerEvent } from '../types/scheduler.types';

/**
 * Pick the event whose claimNumber matches the search and whose start
 * date is closest to today, preferring future (or today) over past so a
 * fresh search lands on the next upcoming occurrence rather than an old
 * one. Returns undefined when no matching events exist.
 *
 * The API now returns the searched claim's tasks plus surrounding
 * context tasks for the assigned resources, so multiple matches are the
 * norm and a deterministic pick rule is required.
 */
export function pickClosestEventToToday(
  events: SchedulerEvent[],
  claimNumber: string | null | undefined,
  /** Optional reference date — defaults to now. Injected by tests. */
  today: Date = new Date()
): SchedulerEvent | undefined {
  if (!claimNumber || !events || events.length === 0) return undefined;

  const matches = events
    .filter(
      (event) =>
        (event as { claimNumber?: string }).claimNumber === claimNumber &&
        event.startDate
    )
    .map((event) => ({
      event,
      startDate: new Date(event.startDate as string | Date),
    }));

  const todayMs = new Date(today).setHours(0, 0, 0, 0);
  const future = matches
    .filter((m) => m.startDate.getTime() >= todayMs)
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  const past = matches
    .filter((m) => m.startDate.getTime() < todayMs)
    .sort((a, b) => b.startDate.getTime() - a.startDate.getTime());

  return (future[0] ?? past[0])?.event;
}

/**
 * Build a 7-day SchedulerDateRange centered on the given date — 3 days
 * before, the date itself, and 3 days after. `endDate` is exclusive
 * (midnight of the day after the last visible day) to match the
 * scheduler's half-open convention.
 */
export function centerWeekOnDate(date: Date | string): SchedulerDateRange {
  const day = startOfDay(typeof date === 'string' ? new Date(date) : date);
  return {
    startDate: subDays(day, 3),
    endDate: addDays(day, 4),
  };
}
